import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, MessagePayload, onMessage } from 'firebase/messaging';
import { ApiClientService, EntityApiService } from '@dfl/react-security';
import { getDeviceId, setPushSubscribed } from 'modules/notification/services/device-id.service';

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Push notification operation timed out'));
      }, ms);
    }),
  ]);

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
};

type RegisterPushTokenPayload = {
  deviceId: string;
  token: string;
  platform: 'web';
  meta: {
    browser: string;
    language: string;
    userAgent: string;
  };
};

// Mirrors GET /firebase/device-token/subscription in ms-auth.
type SubscriptionStatus = {
  registered: boolean;
  active: boolean;
  subscribed: boolean;
  token: string | null;
};

class PushNotificationService extends EntityApiService<RegisterPushTokenPayload> {
  private async getRegistration (): Promise<ServiceWorkerRegistration> {
    return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  }

  private async getMessagingInstance () {
    if (!(await isSupported())) return null;
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    return getMessaging(app);
  }

  async register (requestPermission = false): Promise<boolean> {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;

    // Regenerate the token (POST /device-token) whenever the device is NOT
    // active — this covers both "never registered" and "registered but revoked"
    // (revokedAt set on logout). /subscribe alone only flips subscribed=true; it
    // does NOT clear revokedAt, so relying on `registered` would leave a revoked
    // device silently unable to receive notifications.
    const status = await this.getSubscriptionStatus();
    if (!status.active) {
      const generated = await this.generateAndStoreToken(requestPermission);
      if (!generated) return false;
    }

    await this.handleResponse(ApiClientService.post(this.getPath('/subscribe'), { deviceId: getDeviceId() }));
    setPushSubscribed(true);
    return true;
  }

  private async generateAndStoreToken (requestPermission: boolean): Promise<boolean> {
    const permission = requestPermission ? await Notification.requestPermission() : Notification.permission;
    if (permission !== 'granted') return false;

    const messaging = await this.getMessagingInstance();
    if (!messaging || !process.env.REACT_APP_FIREBASE_VAPID_KEY) return false;

    const token = await withTimeout(
      getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: await this.getRegistration(),
      }),
      15000,
    );
    if (!token) return false;

    const payload: RegisterPushTokenPayload = {
      deviceId: getDeviceId(),
      token,
      platform: 'web',
      meta: {
        browser:
          (
            navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string; version: string }> } }
          ).userAgentData?.brands
            ?.map(({ brand, version }) => `${brand} ${version}`)
            .join(', ') || '',
        language: navigator.language,
        userAgent: navigator.userAgent,
      },
    };

    await this.handleResponse(ApiClientService.post(this.getPath(''), payload));
    return true;
  }

  async getSubscriptionStatus (): Promise<SubscriptionStatus> {
    return await this.handleResponse(
      ApiClientService.get(this.getPath('/subscription'), { params: { deviceId: getDeviceId() } }),
    );
  }

  /**
   * Login sync. Re-registers the device token (POST /device-token) to clear the
   * revokedAt set on logout — never touches /subscribe, so the user's
   * subscribe/unsubscribe preference is preserved. Only reactivates a device
   * that was subscribed but got revoked by logout (registered && subscribed &&
   * !active); leaves unsubscribed devices alone and skips already-active ones.
   * Silent: generateAndStoreToken(false) never prompts for permission.
   */
  async syncOnLogin (): Promise<SubscriptionStatus> {
    const status = await this.getSubscriptionStatus();
    if (status.registered && status.subscribed && !status.active) {
      await this.generateAndStoreToken(false);
    }
    return status;
  }

  async unsubscribe (): Promise<void> {
    // Backend keeps the FCM token intact and only sets subscribed=false, so the
    // token stays valid. Do NOT deleteToken here — re-subscribe just needs /subscribe.
    await this.handleResponse(ApiClientService.post(this.getPath('/unsubscribe'), { deviceId: getDeviceId() }));
    setPushSubscribed(false);
  }

  async unregister (): Promise<void> {
    await this.handleResponse(
      ApiClientService.delete(this.getPath(''), {
        data: { deviceId: getDeviceId() },
      }),
    );
  }

  // async sendTest (): Promise<{ successCount: number; failureCount: number }> {
  //   return await this.handleResponse(ApiClientService.post(this.getPath('/test'), { deviceId: getDeviceId() }));
  // }

  async subscribeToForegroundMessages (handler: (payload: MessagePayload) => void): Promise<() => void> {
    const messaging = await this.getMessagingInstance();
    return messaging ? onMessage(messaging, handler) : () => undefined;
  }
}

export default new PushNotificationService('/ms-auth/api/firebase/device-token');
